import type { ConsentPolicy } from 'WebmcpCore/consent/tier';
import { ConsentDeniedError } from 'WebmcpCore/errors';
import type { ConsentRecord, WebMcpToolDefinition } from 'WebmcpCore/types';

export interface ConsentPromptInput {
  toolName: string;
  description: string;
  preview: string;
}

export type ConsentPromptFn = (input: ConsentPromptInput) => Promise<boolean>;

export interface ConsentGateOptions {
  prompt: ConsentPromptFn;
}

export class ConsentGate {
  private records = new Map<string, ConsentRecord>();

  constructor(private readonly opts: ConsentGateOptions) {}

  async check(input: { tool: WebMcpToolDefinition; args: unknown; policy: ConsentPolicy }): Promise<void> {
    const { tool, args, policy } = input;
    if (!policy.requiresConsent) return;

    if (policy.duration === 'session') {
      const cached = this.records.get(tool.name);
      if (cached?.outcome === 'granted') return;
      if (cached?.outcome === 'denied') {
        throw new ConsentDeniedError('Consent previously denied this session', tool.name);
      }
    }

    const granted = await this.opts.prompt({
      description: tool.description,
      preview: this.preview(tool, args),
      toolName: tool.name,
    });
    this.records.set(tool.name, { at: Date.now(), duration: policy.duration, outcome: granted ? 'granted' : 'denied' });
    if (!granted) {
      throw new ConsentDeniedError('User denied consent', tool.name);
    }
  }

  reset(): void {
    this.records.clear();
  }

  private preview(tool: WebMcpToolDefinition, args: unknown): string {
    if (tool.previewIntent) return tool.previewIntent(args as never);
    return `Invoke ${tool.name}`;
  }
}
